import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { defaultSiteSettings } from "@/data/vomarcData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vomarc_admin_token";

// Session-scoped token (cleared on tab close). Token has a short server-side expiry.
const tokenStore = {
  get: () => { try { return window.sessionStorage.getItem(TOKEN_KEY) || ""; } catch (err) { console.warn("sessionStorage unavailable", err); return ""; } },
  set: (t) => { try { window.sessionStorage.setItem(TOKEN_KEY, t); } catch (err) { console.warn("sessionStorage unavailable", err); } },
  clear: () => {
    try { window.sessionStorage.removeItem(TOKEN_KEY); } catch (err) { console.warn("sessionStorage unavailable", err); }
    try { window.localStorage.removeItem(TOKEN_KEY); } catch (err) { console.warn("localStorage unavailable", err); }
  },
};

export function useAdminSession() {
  const [token, setToken] = useState(() => (typeof window === "undefined" ? "" : tokenStore.get()));
  const [settings, setSettings] = useState(defaultSiteSettings);
  const [demoRequests, setDemoRequests] = useState([]);
  const [investorRequests, setInvestorRequests] = useState([]);
  const [appStates, setAppStates] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const loadSummary = useCallback(async (activeToken = token) => {
    if (!activeToken) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/summary`, { headers: { Authorization: `Bearer ${activeToken}` } });
      setSettings({ ...defaultSiteSettings, ...response.data.settings });
      setDemoRequests(response.data.demo_requests || []);
      setAppStates(response.data.app_states || []);
      setInvestorRequests(response.data.investor_requests || []);
    } catch (err) {
      console.warn("Admin summary failed", err);
      toast.error("Admin session expired. Please login again.");
      tokenStore.clear();
      setToken("");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      tokenStore.set(response.data.token);
      setToken(response.data.token);
      toast.success("Administration login successful");
      await loadSummary(response.data.token);
      return true;
    } catch (err) {
      console.warn("Admin login failed", err);
      toast.error("Invalid administration login");
      return false;
    }
  };

  const logout = () => { tokenStore.clear(); setToken(""); };

  const update = (field, value) => setSettings((current) => ({ ...current, [field]: value }));

  const saveSettings = async () => {
    try {
      const response = await axios.put(`${API}/admin/site-settings`, settings, { headers: authHeaders });
      setSettings({ ...defaultSiteSettings, ...response.data });
      toast.success("Website details updated");
    } catch (err) {
      console.warn("Save settings failed", err);
      toast.error("Unable to save website details");
    }
  };

  const uploadImage = async (field, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const response = await axios.post(`${API}/admin/upload-image`, form, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
      update(field, `${process.env.REACT_APP_BACKEND_URL}${response.data.image_url}`);
      toast.success("Image uploaded. Save website details to publish it.");
    } catch (err) {
      console.warn("Image upload failed", err);
      toast.error("Image upload failed");
    }
  };

  const setRows = (kind, fn) => (kind === "demo" ? setDemoRequests(fn) : setInvestorRequests(fn));
  const enquiryUrl = (kind, id) => `${API}/admin/${kind === "demo" ? "demo-requests" : "investor-requests"}/${id}`;

  const updateStatus = async (kind, id, status, label) => {
    try {
      const response = await axios.patch(enquiryUrl(kind, id), { status }, { headers: authHeaders });
      setRows(kind, (rows) => rows.map((row) => (row.id === id ? response.data : row)));
      toast.success(`Marked as ${label}`);
    } catch (err) {
      console.warn("Status update failed", err);
      toast.error("Could not update status");
    }
  };

  const deleteEnquiry = async (kind, id) => {
    if (!window.confirm("Delete this enquiry permanently? This cannot be undone.")) return;
    try {
      await axios.delete(enquiryUrl(kind, id), { headers: authHeaders });
      setRows(kind, (rows) => rows.filter((row) => row.id !== id));
      toast.success("Enquiry deleted");
    } catch (err) {
      console.warn("Delete enquiry failed", err);
      toast.error("Could not delete enquiry");
    }
  };

  return { token, authHeaders, loading, settings, demoRequests, investorRequests, appStates, loadSummary, login, logout, update, saveSettings, uploadImage, updateStatus, deleteEnquiry };
}
